import { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Card,
  IconButton,
  CardActionArea,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import { aplicarVariables, limpiarTelefono, abrirWhatsApp } from "./waUtils";
import { etapaLabel } from "./pipelineUtils";

export default function EnviarWADialog({ open, onClose, store, lead }) {
  const [mensajeCustom, setMensajeCustom] = useState("");
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState(null);
  const [mensajeEditado, setMensajeEditado] = useState("");
  const [editando, setEditando] = useState(false);

  const plantillas = store.config.plantillasWA || [];

  const plantillasOrdenadas = useMemo(() => {
    const todas = [...plantillas];
    todas.sort((a, b) => (b.favorita ? 1 : 0) - (a.favorita ? 1 : 0));
    if (!lead?.etapa) return todas;
    const porEtapa = todas.filter(p => p.etapa === lead.etapa);
    const sinEtapa = todas.filter(p => !p.etapa);
    const otrasEtapas = todas.filter(p => p.etapa && p.etapa !== lead.etapa);
    return [...porEtapa, ...sinEtapa, ...otrasEtapas];
  }, [plantillas, lead?.etapa]);

  const mensajeFinal = useMemo(() => {
    if (editando && mensajeEditado) return aplicarVariables(mensajeEditado, lead, store.config);
    if (plantillaSeleccionada) return aplicarVariables(plantillaSeleccionada.mensaje, lead, store.config);
    return aplicarVariables(mensajeCustom, lead, store.config);
  }, [plantillaSeleccionada, mensajeCustom, mensajeEditado, editando, lead, store.config]);

  const urlWA = useMemo(() => {
    if (!lead?.telefono || !mensajeFinal) return null;
    const tel = limpiarTelefono(lead.telefono, lead.codigoPais, store.config.codigoPais);
    return `https://wa.me/${tel}?text=${encodeURIComponent(mensajeFinal)}`;
  }, [lead, mensajeFinal, store.config.codigoPais]);

  const handleEnviar = () => {
    if (!urlWA) return;
    if (!abrirWhatsApp(limpiarTelefono(lead.telefono, lead.codigoPais, store.config.codigoPais), mensajeFinal)) {
      window.alert("Permite las ventanas emergentes para abrir WhatsApp.");
      return;
    }
    store.agregarContacto(lead.id, {
      tipo: "whatsapp",
      nota: `Mensaje WA enviado${plantillaSeleccionada ? ` (plantilla: ${plantillaSeleccionada.nombre})` : ""}: ${mensajeFinal.slice(0, 100)}...`,
    });
    handleClose();
  };

  const handleCopiar = () => {
    if (mensajeFinal) navigator.clipboard?.writeText(mensajeFinal);
  };

  const handleSeleccionarPlantilla = (p) => {
    if (plantillaSeleccionada?.id === p.id) {
      setPlantillaSeleccionada(null);
      setEditando(false);
      setMensajeEditado("");
    } else {
      setPlantillaSeleccionada(p);
      setMensajeCustom("");
      setEditando(false);
      setMensajeEditado("");
    }
  };

  const handleEditarMensaje = () => {
    const base = plantillaSeleccionada
      ? aplicarVariables(plantillaSeleccionada.mensaje, lead, store.config)
      : mensajeCustom;
    setMensajeEditado(base);
    setEditando(true);
  };

  const handleClose = () => {
    setMensajeCustom("");
    setPlantillaSeleccionada(null);
    setMensajeEditado("");
    setEditando(false);
    onClose();
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WhatsAppIcon sx={{ color: "#25d366" }} />
        Enviar WhatsApp a {lead.nombre}
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <Typography variant="caption" color="text.secondary">
            Teléfono: {lead.telefono || "No registrado"}
          </Typography>
          <Chip label={etapaLabel(store.config, lead.etapa)} size="small" variant="outlined" />
        </Stack>

        {plantillasOrdenadas.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Plantillas rápidas</Typography>
            <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto" }}>
              {plantillasOrdenadas.map((p) => {
                const esOtraEtapa = p.etapa && p.etapa !== lead.etapa;
                return (
                  <Card key={p.id} variant="outlined" sx={{
                    border: plantillaSeleccionada?.id === p.id ? "2px solid #25d366" : undefined,
                    bgcolor: plantillaSeleccionada?.id === p.id ? "tint.revenue" : undefined,
                    opacity: esOtraEtapa ? 0.5 : 1,
                  }}>
                    <CardActionArea onClick={() => handleSeleccionarPlantilla(p)} sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {p.favorita && <StarIcon sx={{ color: "favorite.main", fontSize: 16 }} />}
                          <Typography variant="body2" fontWeight={600}>{p.nombre}</Typography>
                          {p.etapa && (
                            <Chip label={etapaLabel(store.config, p.etapa)} size="small"
                              color={p.etapa === lead.etapa ? "primary" : "default"}
                              variant={p.etapa === lead.etapa ? "filled" : "outlined"}
                              sx={{ fontSize: 10, height: 18 }}
                            />
                          )}
                        </Stack>
                        {plantillaSeleccionada?.id === p.id && <Chip label="Seleccionada" size="small" color="success" />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        {p.mensaje.slice(0, 80)}{p.mensaje.length > 80 ? "..." : ""}
                      </Typography>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        )}

        {plantillasOrdenadas.length > 0 && !plantillaSeleccionada && (
          <Divider sx={{ my: 2 }}>o escribe un mensaje</Divider>
        )}

        {!plantillaSeleccionada && !editando && (
          <TextField
            label="Mensaje personalizado"
            value={mensajeCustom}
            onChange={(e) => setMensajeCustom(e.target.value)}
            multiline rows={3} fullWidth
            placeholder="Hola {nombre}, te escribo de {negocio}..."
            helperText="Variables: {nombre}, {curso}, {negocio}, {telefono}, {edad}, {precio}"
          />
        )}

        {editando && (
          <TextField
            label="Editar mensaje antes de enviar"
            value={mensajeEditado}
            onChange={(e) => setMensajeEditado(e.target.value)}
            multiline rows={3} fullWidth autoFocus
          />
        )}

        {mensajeFinal && (
          <Box mt={2} p={2} bgcolor="#dcf8c6" borderRadius={2} position="relative">
            <Typography variant="caption" fontWeight={700} color="text.secondary" mb={0.5} display="block">
              Vista previa del mensaje:
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{mensajeFinal}</Typography>
            <Stack direction="row" sx={{ position: "absolute", top: 8, right: 8 }}>
              {!editando && (
                <IconButton size="small" onClick={handleEditarMensaje} title="Editar antes de enviar">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleCopiar} title="Copiar">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={handleEnviar}
          disabled={!urlWA} sx={{ bgcolor: "#25d366", "&:hover": { bgcolor: "#1da851" } }}>
          Enviar por WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
}
